import { Expense, ExpenseKindEnum, TripFolder } from "@finway-group/shared/lib/models"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { getApproverIdsOfApprovalProcesses } from "@finway-group/shared/lib/utils/approvalProcess.utils"
import { Tag, Tooltip } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"

import { useCompany } from "Shared/hooks/company.hooks"
import { useCostCenterById } from "Shared/hooks/costCenter.hooks"
import { ExpenseService, NotificationService, UserService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import { getReimbursementLabel } from "Shared/utils/expense.utils"
import { useEmployee } from "Shared/queries/employee.queries"
import { getNextApproverId } from "Shared/utils/approvalProcess.utils"

import { getSubstitutionTooltipMessage } from "Shared/utils/employee.utils"
import { UserImage } from "./UserImage"
import { ExpenseTagColumn } from "./expenseTagColumn"
import { FolderIcon } from "./icons/FolderIcon"
import { SharedExpenseTag } from "./sharedExpenseTag"

interface RequestColumnWithAvatarInterface {
    request: Expense
    showExpenseNumbers?: boolean
    showNextApprover: boolean
}

export const RequestColumnWithAvatar = React.memo(({ request, showExpenseNumbers = false, showNextApprover }: RequestColumnWithAvatarInterface) => {
    const { t } = useTranslation()

    const loggedInProfile = UserService.getLoggedInEmployeeProfile()
    const employeeToShowId = showNextApprover ? getNextApproverId(request.approvalProcesses) : request.requestedBy._id
    const { data: employeeToShow, isLoading: isEmployeeToShowLoading } = useEmployee(employeeToShowId)
    const isMyExpense = request.requestedBy._id === loggedInProfile.id
    const isTripFolder = request.kind === ExpenseKindEnum.TRIP_FOLDER
    const costCenter = useCostCenterById(request.costCenter)
    const creditor = request.getCreditor()
    const { taggingEnabled } = useCompany()

    const isApproverOfAnyApprovalProcess = getApproverIdsOfApprovalProcesses(request.approvalProcesses).includes(loggedInProfile.id)
    const displayUserAsTagged = ExpenseService.shouldDisplayUserAsTagged(isMyExpense, isApproverOfAnyApprovalProcess, loggedInProfile, request)

    useEffect(() => {
        if (!employeeToShow && !isEmployeeToShowLoading) {
            NotificationService.send(NotificationTypeEnum.WARNING, t("error:error"), t("error:employee.missing"))
        }
    }, [employeeToShow, isEmployeeToShowLoading])

    if (!employeeToShow) {
        return <></>
    }

    const TripFolderDescription = () => (
        <p className="flex items-center gap-6">
            <FolderIcon />
            <span>{showExpenseNumbers && `#${request.expenseNumber}`}</span>
            <span className="tracking-wide">{(request as TripFolder).folderName}</span>
        </p>
    )

    let substitutionTooltip
    if (showNextApprover) {
        substitutionTooltip = getSubstitutionTooltipMessage(employeeToShow, loggedInProfile.id, t)
    }

    return (
        <div className="flex image-loader gap-10 flex-no-wrap">
            <UserImage user={employeeToShow} size="large" showReplacementIcon={!!substitutionTooltip} tooltipTitle={substitutionTooltip} />
            <div className="flex flex-col mr-5 overflow-auto no-scrollbar">
                <p className="text-text-dark font-bold mt-3 mb-3 leading-none flex">
                    <Tooltip title={substitutionTooltip}>
                        <span className={`truncate ${substitutionTooltip ? "p-3 rounded-sm bg-finway-blue bg-opacity-15" : ""}`}>
                            {`${employeeToShow.getFullName()}`} {`(${t(showNextApprover ? "label:approver" : "label:requester")})`}
                        </span>
                    </Tooltip>
                    {isTripFolder && <Tag className="ant-tag-blue ml-10">{t(`label:trip_folder`)}</Tag>}
                    {displayUserAsTagged && <SharedExpenseTag tagging={request.userTagging?.filter(({ tagged }) => tagged === loggedInProfile.id)} />}
                </p>
                <div className="flex">
                    <Tooltip title={request.description}>
                        <div className="text-sm leading-tight truncate min-w-40">
                            {isTripFolder && <TripFolderDescription />}
                            {!isTripFolder && (
                                <>
                                    {showExpenseNumbers && `# ${request.expenseNumber}${request.description || request.isAutoGenerated ? " - " : ""}`} {creditor?.name || "n/a"} (
                                    {costCenter?.name?.trim() || t("label:n/a")})
                                </>
                            )}
                        </div>
                    </Tooltip>

                    {request.kind === ExpenseKindEnum.SUBSCRIPTION && <Tag className="ant-tag-blue ml-10">{t("label:recurring")}</Tag>}
                    {request.isReimbursement && (
                        <Tooltip title={t(`tooltips:reimbursement_types.${getReimbursementLabel(request.kind)}`)}>
                            <Tag className="ant-tag-blue ml-10">{t(`label:reimbursement_types.${getReimbursementLabel(request.kind)}`)}</Tag>{" "}
                        </Tooltip>
                    )}
                    {request.paymentOption === ExpensePaymentOptionEnum.SMART_CARD && <Tag className="ml-10 ant-tag-blue">{t("label:cards.card")}</Tag>}
                    {(request.totalNetPrice < 0 || request.totalGrossPrice < 0) && <Tag className="ml-10 ant-tag-green">{t("label:refund")}</Tag>}
                    {taggingEnabled && request.expenseTags && (
                        <div className="ml-10 h-14">
                            <ExpenseTagColumn expenseTagIds={request.expenseTags} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
})
